/**
 * VerificationBadge — Atlas folder verification indicator.
 *
 * Three states:
 *   • unverified   — small muted gray "?" (tooltip: "Not yet verified")
 *   • verified     — green checkmark (tooltip: "Verified — <date> by <name>")
 *   • needs_review — amber "!" (tooltip: "New content added — needs re-verification")
 *
 * Read-only for advisor / cert_reviewer / traveler. Clickable for
 * atlas_admin — click on any non-verified state POSTs to
 * `/api/atlas/topics/{slug}/verify` and optimistically flips to green.
 * Click on already-verified is a no-op (per PRD — verified only flips
 * back via a real content change).
 *
 * Two sizes:  size="sm" (14px, used in the LeftRailTree row)
 *             size="md" (22px, used in the folder detail header)
 */
import React, { useState, useCallback } from "react";
import { CheckCircle2, AlertCircle, HelpCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATE_META = {
  verified: {
    Icon: CheckCircle2,
    ring: "text-emerald-600",
    bg:   "bg-emerald-50 border-emerald-300",
    hover:"hover:bg-emerald-100",
    label:"Verified",
  },
  needs_review: {
    Icon: AlertCircle,
    ring: "text-amber-600",
    bg:   "bg-amber-50 border-amber-300",
    hover:"hover:bg-amber-100",
    label:"Needs review",
  },
  unverified: {
    Icon: HelpCircle,
    ring: "text-tb-navy/45",
    bg:   "bg-tb-navy/[0.04] border-tb-navy/15",
    hover:"hover:bg-tb-navy/[0.08]",
    label:"Not yet verified",
  },
};

function _fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return null; }
}

function _tooltip(state) {
  if (state?.verification_status === "verified") {
    const when = _fmtDate(state.verified_at);
    const who  = state.verified_by_name || "an admin";
    return `Verified${when ? ` on ${when}` : ""} by ${who}`;
  }
  if (state?.verification_status === "needs_review") {
    const changed = _fmtDate(state.content_changed_at);
    const who     = state.verified_by_name;
    let msg = "New content added — needs re-verification";
    if (who) msg += ` (last verified by ${who})`;
    if (changed) msg += ` · changed ${changed}`;
    return msg;
  }
  return "Not yet verified";
}

export default function VerificationBadge({
  slug,
  status = "unverified",     // "unverified" | "verified" | "needs_review"
  verifiedAt = null,
  verifiedByName = null,
  contentChangedAt = null,
  isAdmin = false,
  size = "sm",               // "sm" (14px) or "md" (22px)
  onVerified = null,         // (fresh) => void  — called after admin verify
}) {
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState({
    verification_status: status,
    verified_at:         verifiedAt,
    verified_by_name:    verifiedByName,
    content_changed_at:  contentChangedAt,
  });
  // Keep local in sync when parent props refresh (poll cycles).
  React.useEffect(() => {
    setLocal({
      verification_status: status,
      verified_at:         verifiedAt,
      verified_by_name:    verifiedByName,
      content_changed_at:  contentChangedAt,
    });
  }, [status, verifiedAt, verifiedByName, contentChangedAt]);

  const clickable = isAdmin && local.verification_status !== "verified";
  const meta = STATE_META[local.verification_status] || STATE_META.unverified;
  const Icon = busy ? Loader2 : meta.Icon;
  const iconClass = busy ? "animate-spin" : "";
  const px = size === "md" ? "px-2 py-1" : "px-1 py-0.5";
  const dim = size === "md" ? "w-[22px] h-[22px]" : "w-[14px] h-[14px]";

  const doVerify = useCallback(async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!clickable || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/atlas/topics/${slug}/verify`);
      const next = {
        verification_status: "verified",
        verified_at:         data.verified_at,
        verified_by_name:    data.verified_by_name,
        content_changed_at:  local.content_changed_at,
      };
      setLocal(next);
      toast.success("Marked as verified");
      onVerified?.(next);
    } catch (err) {
      toast.error("Couldn't verify — try again");
    } finally {
      setBusy(false);
    }
  }, [clickable, busy, slug, local.content_changed_at, onVerified]);

  const badge = (
    <span
      onClick={clickable ? doVerify : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : -1}
      onKeyDown={clickable ? (ev) => {
        if (ev.key === "Enter" || ev.key === " ") { doVerify(ev); }
      } : undefined}
      data-testid={`atlas-verification-badge-${slug}`}
      data-verification-status={local.verification_status}
      title={_tooltip(local)}
      aria-label={_tooltip(local)}
      className={[
        "inline-flex items-center justify-center rounded-full border shrink-0",
        px,
        meta.bg, meta.ring,
        clickable ? `cursor-pointer ${meta.hover} tb-tap` : "cursor-default",
        busy ? "opacity-70" : "",
      ].join(" ")}
    >
      <Icon className={`${dim} ${iconClass}`} strokeWidth={2.2} />
    </span>
  );
  return badge;
}
